type MetaBrowserIdentifiers = {
  fbp?: string
  fbc?: string
}

type MetaFbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue?: unknown[][]
  loaded?: boolean
  version?: string
  push?: (...args: unknown[]) => void
}

declare global {
  interface Window {
    fbq?: MetaFbq
    _fbq?: MetaFbq
    __legalPrimeMetaLastPageView?: {
      key: string
      at: number
    }
    __legalPrimeMetaLastPurchase?: string
  }
}

const META_COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60
const META_SCRIPT_ID = 'legalprime-meta-pixel-script'
const initializedPixels = new Set<string>()

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null

  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`))

  return match ? decodeURIComponent(match[1]) : null
}

function writeCookie(name: string, value: string): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return

  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    'Path=/',
    `Max-Age=${META_COOKIE_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
  ]

  if (window.location.protocol === 'https:') {
    parts.push('Secure')
  }

  document.cookie = parts.join('; ')
}

function sanitizeIdentifier(value: string | null | undefined): string | null {
  const normalized = (value ?? '').trim()
  if (!normalized) return null
  return /^[A-Za-z0-9._%:+\-=/]+$/.test(normalized) ? normalized : null
}

function isValidFbp(value: string | null | undefined): value is string {
  return typeof value === 'string' && /^fb\.1\.\d{10,20}\.[A-Za-z0-9._-]+$/.test(value)
}

function isValidFbc(value: string | null | undefined): value is string {
  return typeof value === 'string' && /^fb\.1\.\d{10,20}\.[A-Za-z0-9._%:+\-=/]+$/.test(value)
}

function buildFbp(): string {
  const random = Math.floor(Math.random() * 10_000_000_000)
  return `fb.1.${Date.now()}.${random}`
}

function buildFbc(fbclid: string): string {
  return `fb.1.${Date.now()}.${fbclid}`
}

function getFbclidFromLocation(): string | null {
  if (typeof window === 'undefined') return null
  return sanitizeIdentifier(new URL(window.location.href).searchParams.get('fbclid'))
}

function ensureMetaScript(): void {
  if (typeof document === 'undefined' || document.getElementById(META_SCRIPT_ID)) {
    return
  }

  const script = document.createElement('script')
  script.id = META_SCRIPT_ID
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  document.head.appendChild(script)
}

function ensureFbq(): MetaFbq | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null

  if (typeof window.fbq === 'function') {
    ensureMetaScript()
    return window.fbq
  }

  const fbq = function (...args: unknown[]) {
    if (typeof fbq.callMethod === 'function') {
      fbq.callMethod(...args)
      return
    }

    fbq.queue = fbq.queue || []
    fbq.queue.push(args)
  } as MetaFbq

  fbq.queue = []
  fbq.loaded = true
  fbq.version = '2.0'
  fbq.push = (...args: unknown[]) => fbq(...args)

  window.fbq = fbq
  window._fbq = fbq
  ensureMetaScript()

  return fbq
}

function shouldTrackPageView(pixelId: string): boolean {
  if (typeof window === 'undefined') return false

  const key = `${pixelId}:${window.location.pathname}${window.location.search}`
  const now = Date.now()
  const last = window.__legalPrimeMetaLastPageView

  if (last && last.key === key && now - last.at < 1000) {
    return false
  }

  window.__legalPrimeMetaLastPageView = { key, at: now }
  return true
}

export function ensureMetaBrowserIdentifiers(): MetaBrowserIdentifiers {
  const identifiers: MetaBrowserIdentifiers = {}

  if (typeof window === 'undefined') {
    return identifiers
  }

  const existingFbp = readCookie('_fbp')
  const existingFbc = readCookie('_fbc')
  const fbclid = getFbclidFromLocation()

  let fbp = isValidFbp(existingFbp) ? existingFbp : null
  if (!fbp) {
    fbp = buildFbp()
    writeCookie('_fbp', fbp)
  }

  let fbc = isValidFbc(existingFbc) ? existingFbc : null
  if (fbclid) {
    fbc = buildFbc(fbclid)
    writeCookie('_fbc', fbc)
  } else if (fbc) {
    writeCookie('_fbc', fbc)
  }

  identifiers.fbp = fbp
  if (fbc) {
    identifiers.fbc = fbc
  }

  return identifiers
}

export function initializeMetaPixel(pixelId: string | null | undefined): void {
  const normalizedPixelId = sanitizeIdentifier(pixelId ?? null)
  ensureMetaBrowserIdentifiers()

  if (!normalizedPixelId) {
    return
  }

  const fbq = ensureFbq()
  if (!fbq) {
    return
  }

  if (!initializedPixels.has(normalizedPixelId)) {
    fbq('init', normalizedPixelId)
    initializedPixels.add(normalizedPixelId)
  }

  if (shouldTrackPageView(normalizedPixelId)) {
    fbq('track', 'PageView')
  }
}

export function trackMetaPurchase(
  pixelId: string | null | undefined,
  eventId: string | null | undefined,
  options?: { value?: number },
): void {
  const normalizedPixelId = sanitizeIdentifier(pixelId ?? null)
  const normalizedEventId = (eventId ?? '').trim()
  if (!normalizedPixelId || !normalizedEventId || typeof window === 'undefined') {
    return
  }

  initializeMetaPixel(normalizedPixelId)

  const fbq = ensureFbq()
  if (!fbq) {
    return
  }

  const lastKey = `${normalizedPixelId}:${normalizedEventId}`
  if (window.__legalPrimeMetaLastPurchase === lastKey) {
    return
  }
  window.__legalPrimeMetaLastPurchase = lastKey

  const params: Record<string, string | number> = {
    currency: 'BRL',
  }
  if (typeof options?.value === 'number' && Number.isFinite(options.value)) {
    params.value = options.value
  }

  fbq('track', 'Purchase', params, { eventID: normalizedEventId })
}
